import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ChatBubbleLeftRightIcon,
  ClipboardDocumentListIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import {
  affirmationPlanAdminService,
  AffirmationPlanAnalytics,
  sentenceInfoService,
  supportService,
} from '../services/apiService';

type SupportSummary = {
  total: number;
  newTickets: number;
  waiting: number;
};

const AdminDashboard: React.FC = () => {
  const { token, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [planStats, setPlanStats] = useState<AffirmationPlanAnalytics | null>(null);
  const [support, setSupport] = useState<SupportSummary>({ total: 0, newTickets: 0, waiting: 0 });
  const [sentenceToday, setSentenceToday] = useState(0);
  const [sentenceTotal, setSentenceTotal] = useState(0);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const [analyticsRes, ticketsRes, sentencesRes] = await Promise.allSettled([
          affirmationPlanAdminService.analytics(token),
          supportService.getTickets(token, { status: 'all', priority: 'all' }),
          sentenceInfoService.list(token, 200),
        ]);

        if (cancelled) return;

        if (analyticsRes.status === 'fulfilled') {
          setPlanStats(analyticsRes.value.data ?? null);
        }

        if (ticketsRes.status === 'fulfilled') {
          const ticketPayload = ticketsRes.value.data as { items?: any[] } | any[] | undefined;
          const tickets = Array.isArray(ticketPayload) ? ticketPayload : ticketPayload?.items ?? [];
          const newTickets = tickets.filter((t: any) => t.status === 0 || t.status === 1).length;
          const waiting = tickets.filter((t: any) => t.status === 2 || t.status === 3).length;
          setSupport({ total: tickets.length, newTickets, waiting });
        }

        if (sentencesRes.status === 'fulfilled') {
          const data = sentencesRes.value.data as { items?: any[] } | undefined;
          const items = data?.items ?? [];
          setSentenceTotal(items.length);
          const today = new Date();
          const y = today.getFullYear();
          const m = today.getMonth();
          const d = today.getDate();
          const todayCount = items.filter((row) => {
            if (!row.sDate) return false;
            const dt = new Date(row.sDate);
            return !Number.isNaN(dt.getTime()) && dt.getFullYear() === y && dt.getMonth() === m && dt.getDate() === d;
          }).length;
          setSentenceToday(todayCount);
        }
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Özet yüklenemedi');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const todayLabel = new Date().toLocaleDateString('tr-TR', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  return (
    <div className="p-4 sm:p-6 lg:p-8">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Günün özeti</h1>
        <p className="mt-1 text-sm text-gray-600">
          {todayLabel}
          {user && <span className="ml-2 text-purple-600">— {user.name || user.email}</span>}
        </p>
      </div>

      {error && (
        <div className="mb-6 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          {error}
        </div>
      )}

      {loading ? (
        <p className="text-gray-500">Özet yükleniyor…</p>
      ) : (
        <>
          <div className="mb-8 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <SummaryCard
              title="Onay bekleyen program"
              value={planStats?.pendingApproval ?? 0}
              hint="21 günlük plan — onay kuyruğu"
              tone="amber"
              to="/admin/affirmation-plans"
            />
            <SummaryCard
              title="Aktif program"
              value={planStats?.activePlans ?? 0}
              hint="Şu an devam eden"
              tone="green"
              to="/admin/affirmation-plans"
            />
            <SummaryCard
              title="Bugün eklenen afirmasyon"
              value={sentenceToday}
              hint={`Havuzda son ${sentenceTotal} kayıt listelendi`}
              tone="purple"
              to="/admin/sentence-info"
            />
            <SummaryCard
              title="Açık / bekleyen destek"
              value={support.newTickets + support.waiting}
              hint={`Toplam görünen: ${support.total}`}
              tone="blue"
              to="/admin/support"
            />
          </div>

          <div className="mb-8 grid gap-4 lg:grid-cols-3">
            <SectionCard
              title="21 Günlük Programlar"
              icon={ClipboardDocumentListIcon}
              to="/admin/affirmation-plans"
              items={[
                ['Onay bekleyen', planStats?.pendingApproval ?? 0],
                ['Aktif', planStats?.activePlans ?? 0],
                ['Tamamlanan', planStats?.completedPlans ?? 0],
                ['Reddedilen', planStats?.rejectedPlans ?? 0],
                ['Üretim hatası', planStats?.generationFailed ?? 0],
                ['Toplam talep', planStats?.totalRequests ?? 0],
              ]}
            />
            <SectionCard
              title="Afirmasyon havuzu"
              icon={SparklesIcon}
              to="/admin/sentence-info"
              items={[
                ['Bugün eklenen', sentenceToday],
                ['Listelenen kayıt', sentenceTotal],
              ]}
            />
            <SectionCard
              title="Destek"
              icon={ChatBubbleLeftRightIcon}
              to="/admin/support"
              items={[
                ['Yeni / açık', support.newTickets],
                ['Bekleyen', support.waiting],
                ['Listelenen ticket', support.total],
              ]}
            />
          </div>

          <div className="rounded-xl border border-gray-100 bg-white p-5 shadow-sm">
            <h2 className="mb-3 text-sm font-semibold text-gray-900">Hızlı erişim</h2>
            <div className="flex flex-wrap gap-2">
              <QuickLink to="/admin/affirmation-plans" label="Onay bekleyenleri aç" />
              <QuickLink to="/admin/sentence-info" label="Afirmasyon ekle" />
              <QuickLink to="/admin/support" label="Destek paneli" />
            </div>
          </div>
        </>
      )}
    </div>
  );
};

function SummaryCard({
  title,
  value,
  hint,
  tone,
  to,
}: {
  title: string;
  value: number;
  hint: string;
  tone: 'amber' | 'green' | 'purple' | 'blue';
  to: string;
}) {
  const tones = {
    amber: 'bg-amber-50 border-amber-100 text-amber-900',
    green: 'bg-emerald-50 border-emerald-100 text-emerald-900',
    purple: 'bg-purple-50 border-purple-100 text-purple-900',
    blue: 'bg-sky-50 border-sky-100 text-sky-900',
  };
  return (
    <Link to={to} className={`rounded-xl border p-5 shadow-sm transition hover:shadow-md ${tones[tone]}`}>
      <div className="text-sm font-medium opacity-80">{title}</div>
      <div className="mt-2 text-3xl font-bold">{value}</div>
      <div className="mt-1 text-xs opacity-70">{hint}</div>
    </Link>
  );
}

function SectionCard({
  title,
  icon: Icon,
  to,
  items,
}: {
  title: string;
  icon: React.ComponentType<{ className?: string }>;
  to: string;
  items: [string, number][];
}) {
  return (
    <div className="rounded-xl border border-gray-100 bg-white p-5 shadow-sm">
      <div className="mb-4 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Icon className="h-5 w-5 text-purple-600" />
          <h2 className="font-semibold text-gray-900">{title}</h2>
        </div>
        <Link to={to} className="text-xs font-medium text-purple-600 hover:underline">
          Aç →
        </Link>
      </div>
      <ul className="space-y-2 text-sm">
        {items.map(([label, value]) => (
          <li key={label} className="flex items-center justify-between gap-3">
            <span className="text-gray-600">{label}</span>
            <span className="font-semibold text-gray-900">{value}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function QuickLink({ to, label }: { to: string; label: string }) {
  return (
    <Link
      to={to}
      className="rounded-lg bg-purple-600 px-4 py-2 text-sm font-medium text-white hover:bg-purple-700"
    >
      {label}
    </Link>
  );
}

export default AdminDashboard;
